import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";
import { createRoot } from "react-dom/client";

import { DefaultCatchBoundary } from "./components/default-catch-boundary";
import { NotFound } from "./components/not-found";
import { ThemeProvider } from "./components/theme-provider";
import "./env";

import "./index.css";
import {
  convexClient,
  convexQueryClient,
} from "./integrations/convex/provider";
import { AuthRuntimeProvider } from "./integrations/vortex-auth/runtime";
import { PostHogProvider, PostHogUserSync } from "./lib/posthog-provider";
import type { AuthState } from "./routes/__root";
import { routeTree } from "./routeTree.gen";

// Create QueryClient with Convex's queryFn as the default
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: convexQueryClient.queryFn(),
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
});

// Connect Convex query client to React Query synchronously at module init
convexQueryClient.connect(queryClient);

// Default auth state (will be overridden by InnerApp)
const defaultAuth: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  userId: null,
  orgId: null,
};

// Create router with context
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    convexClient,
    convexQueryClient,
    auth: defaultAuth,
  },
  defaultPreload: "intent", // Preload on hover
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: DefaultCatchBoundary,
  defaultNotFoundComponent: NotFound,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Inner component that bridges vortex-auth's Convex auth state into the router
// context. Route guards (`_authenticated.tsx`) source auth directly from the
// vortex-auth client via `authClient.getSession()`, so this context is
// informational; we still keep it accurate so any consumer reads the truth.
function InnerApp() {
  const { isAuthenticated, isLoading } = useConvexAuth();

  const auth: AuthState = {
    isAuthenticated,
    isLoading,
    // userId/orgId now come from vortex-auth surfaces (useCurrentUser /
    // useActiveOrganization) at the call sites, not the router context. They are
    // left null here; the adapter hooks are the source of truth in F3.
    userId: null,
    orgId: null,
  };

  return <RouterProvider context={{ auth }} router={router} />;
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);
root.render(
  <PostHogProvider>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthRuntimeProvider convex={convexClient}>
          {/* Vortex-auth-backed PostHog user identification (restored in F3). */}
          <PostHogUserSync />
          <InnerApp />
        </AuthRuntimeProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </PostHogProvider>
);
